import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Tooltip,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaInfoCircle } from 'react-icons/fa';
import blindboxService, { BlindboxRatesResponse, ItemRateStats } from '../services/blindboxService';

interface BlindboxRatesStatsProps {
  onRefresh?: () => void;
}

const BlindboxRatesStats: React.FC<BlindboxRatesStatsProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ratesData, setRatesData] = useState<BlindboxRatesResponse | null>(null);
  const boxBg = useColorModeValue('white', 'gray.700');

  // Format percent with specified sign
  const formatPercent = (value: number, alwaysShowSign = false): string => {
    const formatted = (value * 100).toFixed(2) + '%';
    if (alwaysShowSign && value > 0) {
      return '+' + formatted;
    }
    return formatted;
  };

  // Format numeric values
  const formatNumber = (value: number): string => {
    return value.toFixed(4);
  };

  useEffect(() => {
    fetchRatesData();
  }, []);

  const fetchRatesData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await blindboxService.getBlindboxActualRates();
      setRatesData(data);
    } catch (err) {
      console.error('Error fetching blindbox rates data:', err);
      setError('Failed to load blindbox rates data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Get color based on item rarity
  const getItemColor = (name: string): string => {
    switch (name) {
      case 'Common':
        return 'gray';
      case 'Standard':
        return 'blue';
      case 'Uncommon':
        return 'green';
      case 'Rare':
        return 'purple';
      case 'Epic':
        return 'pink';
      case 'Legendary':
        return 'orange';
      default:
        return 'teal';
    }
  };

  // Get color based on difference (green for positive, red for negative)
  const getDifferenceColor = (value: number): string => {
    if (value > 0.005) return 'green';
    if (value < -0.005) return 'red';
    return 'gray';
  };

  if (loading) {
    return (
      <Flex justifyContent="center" py={8}>
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  if (!ratesData || ratesData.totalTransactions === 0) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        No blindbox purchase data available yet. Start purchasing blindboxes to see statistics!
      </Alert>
    );
  }

  const { globalStats, itemStats } = ratesData;

  return (
    <Box>
      <Heading size="md" mb={4}>
        Blindbox Item Drop Rates Analysis
      </Heading>
      <Text mb={4} color="gray.500">
        Based on {globalStats.totalPurchases} blindbox purchases recorded in the system.
      </Text>

      {/* Global Stats */}
      <Box bg={boxBg} p={4} borderRadius="lg" boxShadow="sm" mb={6}>
        <Heading size="sm" mb={4}>
          Global Statistics
        </Heading>
        
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          {/* State Distribution */}
          <Stat>
            <StatLabel>Normal State Purchases</StatLabel>
            <StatNumber>{globalStats.normalStateCount}</StatNumber>
            <StatHelpText>
              {formatPercent(globalStats.normalStatePct / 100)} of total
            </StatHelpText>
          </Stat>
          
          <Stat>
            <StatLabel>Adjusted State Purchases</StatLabel>
            <StatNumber>{globalStats.adjustedStateCount}</StatNumber>
            <StatHelpText>
              {formatPercent(globalStats.adjustedStatePct / 100)} of total
            </StatHelpText>
          </Stat>
          
          {/* Average Return */}
          <Stat>
            <StatLabel>Actual Average Return</StatLabel>
            <StatNumber>{formatNumber(globalStats.actualAverageReturn)} SOL</StatNumber>
            <StatHelpText>
              <StatArrow 
                type={globalStats.actualAverageReturn >= 0.1 ? 'increase' : 'decrease'} 
              />
              {((globalStats.actualAverageReturn / 0.1) * 100 - 100).toFixed(1)}% of cost
            </StatHelpText>
          </Stat>
          
          {/* Expected Return */}
          <Stat>
            <StatLabel>Expected Return (Theoretical)</StatLabel>
            <Flex alignItems="center">
              <StatNumber>
                {formatNumber(globalStats.theoreticalNormalEV)} SOL
              </StatNumber>
              <Tooltip label="Expected value based on item probabilities in normal state">
                <Box ml={1} color="gray.500">
                  <FaInfoCircle size="0.8em" />
                </Box>
              </Tooltip>
            </Flex>
            <StatHelpText>
              Adjusted: {formatNumber(globalStats.theoreticalAdjustedEV)} SOL
            </StatHelpText>
          </Stat>
        </SimpleGrid>
      </Box>

      {/* Item Rates Tabs */}
      <Box bg={boxBg} p={4} borderRadius="lg" boxShadow="sm" overflowX="auto">
        <Tabs variant="enclosed" colorScheme="brand">
          <TabList>
            <Tab>All Data</Tab>
            <Tab>Normal State</Tab>
            <Tab>Adjusted State</Tab>
            <Tab>Comparison</Tab>
          </TabList>

          <TabPanels>
            {/* All Data Tab */}
            <TabPanel px={0}>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Item</Th>
                    <Th>Value</Th>
                    <Th>Occurrences</Th>
                    <Th>Actual Rate</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {itemStats.map((item) => (
                    <Tr key={item.name}>
                      <Td>
                        <Badge colorScheme={getItemColor(item.name)}>
                          {item.name}
                        </Badge>
                      </Td>
                      <Td>{item.value.toFixed(2)} SOL</Td>
                      <Td>{item.count}</Td>
                      <Td>{formatPercent(item.actualRate)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>

            {/* Normal State Tab */}
            <TabPanel px={0}>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Item</Th>
                    <Th>Occurrences</Th>
                    <Th>Actual Rate</Th>
                    <Th>Theoretical Rate</Th>
                    <Th>Difference</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {itemStats.map((item) => (
                    <Tr key={item.name}>
                      <Td>
                        <Badge colorScheme={getItemColor(item.name)}>
                          {item.name}
                        </Badge>
                      </Td>
                      <Td>{item.normalState.count}</Td>
                      <Td>{formatPercent(item.normalState.actualRate)}</Td>
                      <Td>{formatPercent(item.normalState.theoreticalRate)}</Td>
                      <Td>
                        <Text color={getDifferenceColor(item.normalState.difference)}>
                          {formatPercent(item.normalState.difference, true)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>

            {/* Adjusted State Tab */}
            <TabPanel px={0}>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Item</Th>
                    <Th>Occurrences</Th>
                    <Th>Actual Rate</Th>
                    <Th>Theoretical Rate</Th>
                    <Th>Difference</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {itemStats.map((item) => (
                    <Tr key={item.name}>
                      <Td>
                        <Badge colorScheme={getItemColor(item.name)}>
                          {item.name}
                        </Badge>
                      </Td>
                      <Td>{item.adjustedState.count}</Td>
                      <Td>{formatPercent(item.adjustedState.actualRate)}</Td>
                      <Td>{formatPercent(item.adjustedState.theoreticalRate)}</Td>
                      <Td>
                        <Text color={getDifferenceColor(item.adjustedState.difference)}>
                          {formatPercent(item.adjustedState.difference, true)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>

            {/* Comparison Tab */}
            <TabPanel px={0}>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Item</Th>
                    <Th>Value</Th>
                    <Th>Normal Rate</Th>
                    <Th>Adjusted Rate</Th>
                    <Th>Rate Difference</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {itemStats.map((item) => (
                    <Tr key={item.name}>
                      <Td>
                        <Badge colorScheme={getItemColor(item.name)}>
                          {item.name}
                        </Badge>
                      </Td>
                      <Td>{item.value.toFixed(2)} SOL</Td>
                      <Td>{formatPercent(item.normalState.actualRate)}</Td>
                      <Td>{formatPercent(item.adjustedState.actualRate)}</Td>
                      <Td>
                        <Text 
                          color={getDifferenceColor(
                            item.normalState.actualRate - item.adjustedState.actualRate
                          )}
                        >
                          {formatPercent(
                            item.normalState.actualRate - item.adjustedState.actualRate, 
                            true
                          )}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      <Box mt={6} color="gray.500" fontSize="sm">
        <Text>
          Note: Statistical significance depends on the total number of blindbox purchases. The more data collected, the more reliable these statistics become.
        </Text>
      </Box>
    </Box>
  );
};

export default BlindboxRatesStats;
